import React, { useState, useMemo, useEffect } from 'react';
import { DocumentTextIcon, VideoCameraIcon, DownloadIcon } from '../components/icons';
import { Resource } from '../types';
import { resourceApi } from '../services/api';
import SectionTitle from '../components/SectionTitle';
import { useTheme } from '../contexts/ThemeContext';

const ResourcesPage: React.FC = () => {
  const { colors } = useTheme();
  const [activeCategory, setActiveCategory] = useState<string>('All');
  const [resources, setResources] = useState<Resource[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const fetchResources = async () => {
      try {
        setLoading(true);
        const response = await resourceApi.getAllResources();
        setResources(response.data.data.resources);
        setError(null);
      } catch (err) {
        setError('Failed to load resources. Please try again later.');
      } finally {
        setLoading(false);
      }
    };

    fetchResources();
  }, []);

  const categories = useMemo(() => 
    ['All', ...new Set(resources.map(resource => resource.category))], 
    [resources]
  );
  
  const filteredResources = useMemo(() => {
    if (activeCategory === 'All') {
      return resources;
    }
    return resources.filter(resource => resource.category === activeCategory);
  }, [activeCategory, resources]);

  const documentResources = useMemo(() => 
    filteredResources.filter(resource => resource.type === 'document'),
    [filteredResources]
  );

  const videoResources = useMemo(() => 
    filteredResources.filter(resource => resource.type === 'video'),
    [filteredResources]
  );

  const ResourceCard: React.FC<{ resource: Resource }> = ({ resource }) => (
    <div className="rounded-lg p-4 transition-all duration-300 hover:shadow-lg"
         style={{ 
           backgroundColor: colors.background.paper,
           boxShadow: `0 2px 10px -5px ${colors.accent.mint}20`
         }}>
      <div className="flex items-start gap-3">
        <div className="flex-shrink-0">
          {resource.type === 'document' ? (
            <DocumentTextIcon className="w-5 h-5" style={{ color: colors.accent.mint }} />
          ) : (
            <VideoCameraIcon className="w-5 h-5" style={{ color: colors.accent.mint }} />
          )}
        </div>
        <div className="flex-grow min-w-0">
          <h3 className="text-sm font-medium truncate" style={{ color: colors.text.primary }}>{resource.title}</h3>
          <p className="text-xs mt-1 line-clamp-2" style={{ color: colors.text.secondary }}>{resource.description}</p>
        </div>
        <a
          href={resource.fileUrl}
          download={resource.type === 'document'}
          target={resource.type === 'video' ? '_blank' : undefined}
          className="flex-shrink-0 inline-flex items-center justify-center w-8 h-8 rounded-full transition-colors"
          style={{ 
            backgroundColor: `${colors.accent.mint}20`,
            color: colors.accent.mint
          }}
          onMouseEnter={(e) => {
            e.currentTarget.style.backgroundColor = `${colors.accent.mint}30`;
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.backgroundColor = `${colors.accent.mint}20`;
          }}
        >
          <DownloadIcon className="w-4 h-4" />
        </a>
      </div>
    </div>
  );

  const ResourceSection: React.FC<{ title: string; icon: React.ReactNode; resources: Resource[] }> = ({ 
    title, 
    icon, 
    resources 
  }) => (
    <div className="mb-12">
      <div className="flex items-center gap-2 mb-6">
        <div className="p-2 rounded-lg" style={{ backgroundColor: `${colors.primary.main}10` }}>
          {icon}
        </div>
        <h2 className="text-xl font-semibold" style={{ color: colors.text.primary }}>{title}</h2>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {resources.map(resource => (
          <ResourceCard key={resource._id} resource={resource} />
        ))}
      </div>
    </div>
  );

  if (loading) {
    return (
      <div className="container mx-auto px-4 py-12">
        <div className="flex justify-center items-center min-h-[400px]">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2" style={{ borderColor: colors.accent.mint }}></div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="container mx-auto px-4 py-12">
        <div className="text-center">
          <p style={{ color: colors.error.main }}>{error}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="container mx-auto px-4 py-12">
      <SectionTitle 
        title="Resource Library" 
        subtitle="Access valuable documents, forms, and learning materials." 
      />

      {/* Category Filters */}
      <div className="flex flex-wrap justify-center gap-2 mb-12">
        {categories.map(category => {
          const isActive = activeCategory === category;
          console.log(`Category: ${category}, Active: ${activeCategory}, IsActive: ${isActive}`);
          return (
            <button
              key={category}
              onClick={() => {
                console.log('Setting active category to:', category);
                setActiveCategory(category);
              }}
              className={`px-4 py-1.5 rounded-full text-sm font-medium transition-colors duration-200 ${
                isActive 
                  ? 'cursor-default' 
                  : 'cursor-pointer hover:opacity-75'
              }`}
              style={{
                backgroundColor: isActive 
                  ? colors.primary.main 
                  : colors.background.paper,
                color: isActive 
                  ? colors.primary.contrastText
                  : colors.text.secondary,
                border: `1px solid ${isActive ? colors.primary.main : colors.border}`
              }}
            >
              {category}
            </button>
          );
        })}
      </div>

      {/* Documents Section */}
      {documentResources.length > 0 && (
        <ResourceSection
          title="Documents"
          icon={<DocumentTextIcon className="w-6 h-6" style={{ color: colors.accent.mint }} />}
          resources={documentResources}
        />
      )}

      {/* Videos Section */}
      {videoResources.length > 0 && (
        <ResourceSection
          title="Video Resources"
          icon={<VideoCameraIcon className="w-6 h-6" style={{ color: colors.accent.mint }} />}
          resources={videoResources}
        />
      )}

      {/* Empty State */}
      {filteredResources.length === 0 && (
        <div className="text-center py-12">
          <p style={{ color: colors.text.secondary }}>No resources found for the selected category.</p>
        </div>
      )}
    </div>
  );
};

export default ResourcesPage;
    